<!-- Chat message bubble layout with avatar, label, content, timestamp, and actions slots. -->

<script lang="ts" setup>
import type { DateInput } from '@nzyme/types/Date.js';
import { useLanguage } from '@nzyme/vue-i18n/useLanguage.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import type { VNodeChild } from 'vue';

import { formatRelativeDate } from '@sigma/utils/format/formatRelativeDate.js';

// Types
type MessageColor = 'blue' | 'white';
type MessageAlignment = 'left' | 'right';

// Props
const props = defineProps({
  color: defineProp<MessageColor>(),
  align: defineProp<MessageAlignment>(),
  timestamp: defineProp<DateInput>(),
  label: String,
  isFirst: { type: Boolean, default: true },
  isLast: { type: Boolean, default: true },
});

const slots = defineSlots<{
  actions: () => VNodeChild;
  avatar: () => VNodeChild;
  content: () => VNodeChild;
  top: () => VNodeChild;
}>();

const lang = useLanguage();
</script>

<template>
  <div
    :class="[
      css.message,
      css[`variant-${color}`],
      align === 'right' && css.right,
      props.isFirst && css.isFirst,
      props.isLast && css.isLast,
    ]"
  >
    <div :class="css.avatarContainer">
      <slot
        v-if="isLast"
        name="avatar"
      />
    </div>

    <!-- Message content -->
    <div :class="css.inner">
      <div
        v-if="slots.content || slots.top"
        :class="css.bubble"
      >
        <p
          v-if="label"
          :class="css.label"
        >
          {{ label }}
        </p>

        <div :class="css.content">
          <slot name="top" />
          <slot
            v-if="slots.content"
            name="content"
          />
        </div>
      </div>
    </div>

    <div
      v-if="(timestamp && isLast) || slots.actions"
      :class="css.footer"
    >
      <span
        v-if="timestamp && isLast"
        :class="css.timestamp"
      >
        {{ formatRelativeDate(timestamp, lang, { includeTime: true }) }}
      </span>

      <div
        v-if="slots.actions"
        :class="css.actions"
      >
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module="css">
$avatar-size: 32px;
$avatar-margin: $spacing-12;

.message {
  // Two-column grid: a fixed-width avatar slot and a single flexible content
  // track. `minmax(0, 1fr)` is what makes the content track honor the available
  // container width — without the `0` minimum, the track would grow to its
  // children's max-content (e.g. a 260px image) and overflow narrow containers.
  //
  // The footer (timestamp + actions) sits in its own row beneath the content so
  // the avatar's grid cell spans only the bubble row — that's what lets
  // `align-self: flex-end` align the avatar to the bottom of the bubble rather
  // than dropping it down next to the actions.
  display: grid;
  grid-template-columns: $avatar-size + $avatar-margin minmax(0, 1fr);
  grid-template-areas:
    'avatar content'
    '. footer';
  padding: $spacing-04 0;

  --label-background: #{$color-white};
  --message-background: #{$color-doctor-100};

  --message-border: #{$color-doctor-100};
  --message-radius: #{$border-radius-20};
  --message-radius-last: #{$border-radius-20} #{$border-radius-20} #{$border-radius-20} 0;

  &.isFirst {
    padding-top: $spacing-16;
  }

  &.isLast {
    padding-bottom: $spacing-16;
    --message-radius: var(--message-radius-last);
  }
}

// Variant styles
.variant {
  &-white {
    --message-background: #{$color-white};
    --message-border: #{$color-doctor-200};
  }
}

.right {
  grid-template-columns: minmax(0, 1fr) $avatar-size + $avatar-margin;
  grid-template-areas:
    'content avatar'
    'footer .';
  --message-radius-last: #{$border-radius-20} #{$border-radius-20} 0 #{$border-radius-20};

  .inner {
    justify-self: end;
    align-items: flex-end;
  }

  .footer {
    justify-self: end;
    flex-direction: row-reverse;
  }
}

.avatarContainer {
  grid-area: avatar;
  align-self: flex-end;
  padding-right: $avatar-margin;
  --avatar-size: #{$avatar-size};

  .right & {
    padding-left: $avatar-margin;
    padding-right: 0;
  }
}

.inner {
  // The bubble lives here. `justify-self: start` keeps it shrink-to-fit within
  // the constrained content track, and `max-width: 100%` clamps it to the
  // track when content (e.g. a 320px image grid) would otherwise overflow.
  grid-area: content;
  justify-self: start;
  line-height: 1.6;
  max-width: min(100%, 536px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $spacing-08;
  min-width: 0;
}

.bubble {
  line-height: 1.6;
  word-wrap: break-word;
  background-color: var(--label-background);
  border-radius: var(--message-radius);
  border: 1px solid var(--message-border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  // Without an explicit constraint here, `.bubble` would shrink to fit its
  // smallest child, which means image-only bubbles would collapse before the
  // grid had a chance to constrain the tiles. `align-self: stretch` makes the
  // bubble fill `.inner`, so children inherit a constrained width.
  align-self: stretch;
  max-width: 100%;
}

.label {
  color: $color-doctor-700;
  padding: $spacing-04 $spacing-20;
  font-size: $font-size-14;
  text-align: center;
}

.content {
  padding: $spacing-16 $spacing-20;
  background: var(--message-background);
  display: flex;
  flex-direction: column;
  gap: $spacing-12;
  border-radius: var(--message-radius);
  min-width: 0;
}

.footer {
  // Shrink-wrapped to its contents (timestamp + actions) and pinned to the
  // bubble's leading edge via `justify-self`, so it tracks the bubble rather
  // than spreading across the full content column.
  grid-area: footer;
  justify-self: start;
  display: flex;
  gap: $spacing-08;
  align-items: center;
  margin-top: $spacing-04;
}

.actions {
  display: flex;
  gap: $spacing-08;
  align-items: center;
}

.timestamp {
  font-size: $font-size-12;
  color: $color-text-secondary;
}

// Responsive adjustments
@media (max-width: 768px) {
  .message {
    padding: $spacing-12 0;

    &.isFirst {
      padding-top: $spacing-12;
    }

    &.isLast {
      padding-bottom: $spacing-12;
    }

    &:not(.isLast) {
      padding-bottom: $spacing-04;
    }

    &:not(.isFirst) {
      padding-top: $spacing-04;
    }
  }
}
</style>
